export const securityPosts = [
  {
    id: 36,
    slug: "securing-web-apps-owasp-top-10",
    title: "OWASP Top 10 for 2024",
    excerpt: "The ultimate guide to defending your web applications against the most critical security risks.",
    date: "October 01, 2024",
    readTime: "15 min read",
    category: "Security",
    image: "https://images.unsplash.com/photo-1550751827-4bd374c3f58b?auto=format&fit=crop&q=80&w=1200",
    author: "Dishant Madariya",
    content: `
      <h2>The Defender's Handbook</h2>
      <p>The OWASP Top 10 is the standard for web security awareness. For 2024, the landscape has shifted, and developers must understand the latest attack vectors and how to mitigate them proactively.</p>
      <h3>Broken Access Control</h3>
      <p>This remains the #1 risk. We'll show you how to implement robust Role-Based Access Control (RBAC) and why checking permissions on the server for every single request is non-negotiable.</p>
      <h3>Cryptographic Failures</h3>
      <p>In the era of privacy, protecting sensitive data is paramount. Learn why <code>bcrypt</code> is better than <code>sha256</code> for passwords and how to manage encryption keys safely away from your application code.</p>
      <h3>Injection and XSS</h3>
      <p>We'll revisit the classics. Discover how modern frameworks like React protect you by default, but also where they leave you vulnerable, especially when using <code>dangerouslySetInnerHTML</code> or certain browser APIs.</p>
    `,
    keywords: "Security, OWASP, Web Safety"
  },
  {
    id: 37,
    slug: "jwt-vs-sessions-security-showdown",
    title: "JWT vs Sessions: The Security Reality",
    excerpt: "Ditch the hype. Learn which authentication method is actually more secure for your specific application.",
    date: "October 05, 2024",
    readTime: "12 min read",
    category: "Security",
    image: "https://images.unsplash.com/photo-1563986768609-322da13575f3?auto=format&fit=crop&q=80&w=1200",
    author: "Dishant Madariya",
    content: `
      <h2>The Auth Dilemma</h2>
      <p>Authentication is the front door of your app. Whether you use JSON Web Tokens (JWT) or traditional Sessions has massive implications for your security model and your ability to logout users instantly.</p>
      <h3>The Dangers of LocalStorage</h3>
      <p>Learn why storing tokens in <code>localStorage</code> is an XSS nightmare. We'll show you the "HttpOnly" cookie alternative and how it protects your users from malicious scripts stealing their credentials.</p>
      <h3>Token Revocation</h3>
      <p>The biggest weakness of JWTs is their stateless nature. What happens when a user's phone is stolen? We'll discuss "Blacklisting," "Refresh Tokens," and hybrid strategies for revoking tokens in real-time.</p>
      <h3>Best of Both Worlds</h3>
      <p>We'll look at modern patterns that combine the scalability of JWTs with the security of sessions, helping you build a system that is both fast for your servers and safe for your users.</p>
    `,
    keywords: "JWT, Authentication, Cookies, Security"
  },
  {
    id: 38,
    slug: "building-secure-api-gateways",
    title: "Secure API Gateways",
    excerpt: "Protect your microservices behind a rock-solid gateway that handles auth, rate limiting, and threat detection.",
    date: "October 10, 2024",
    readTime: "11 min read",
    category: "Security",
    image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=1200",
    author: "Dishant Madariya",
    content: `
      <h2>The Gatekeeper</h2>
      <p>Your microservices shouldn't be exposed to the public internet. An API Gateway acts as a central shield, managing entry points and enforcing security policies before a request ever reaches your sensitive logic.</p>
      <h3>Rate Limiting and Throttling</h3>
      <p>Learn how to prevent DDoS attacks and "greedy" users from crashing your servers. We'll show you how to implement token-bucket algorithms that protect your resources while ensuring a fair experience for all users.</p>
      <h3>TLS Termination and IPS</h3>
      <p>Discover the role of the gateway in encryption. We'll discuss how it handles HTTPS termination and how to integrate Intrusion Prevention Systems (IPS) that can automatically block suspicious patterns in real-time.</p>
      <h3>Consolidating Authorization</h3>
      <p>Moving authorization to the edge simplifies your microservices. We'll show you how the gateway can verify identities and pass "trusted" user headers to your backend, keeping your internal code clean and focused.</p>
    `,
    keywords: "API Gateway, Microservices Security, Rate Limiting"
  },
  {
    id: 39,
    slug: "zero-trust-architecture-for-devs",
    title: "Zero Trust for Developers",
    excerpt: "Learn why 'Trust but Verify' is dead. Master the principles of Zero Trust to build truly secure networks.",
    date: "October 15, 2024",
    readTime: "13 min read",
    category: "Security",
    image: "https://images.unsplash.com/photo-1550751827-4bd374c3f58b?auto=format&fit=crop&q=80&w=1200",
    author: "Dishant Madariya",
    content: `
      <h2>Never Trust, Always Verify</h2>
      <p>The traditional "perimiter" model of security is broken. Once an attacker is inside your network, they shouldn't have free reign. Zero Trust treats every service and every request as if it was on the public internet.</p>
      <h3>Identity as the New Perimeter</h3>
      <p>Learn why network location is no longer a valid form of trust. We'll discuss the role of strong authentication and "Machine-to-Machine" identity in a zero-trust world.</p>
      <h3>Micro-Segmentation</h3>
      <p>Discover how to divide your network into tiny, isolated zones. We'll show you how to use service meshes or VPC security groups to ensure that Service A can *only* talk to Service B, and nothing else.</p>
      <h3>Continuous Verification</h3>
      <p>Zero Trust isn't a one-time check. We'll explore strategies for continuously verifying the "stance" of a user or a service throughout their session, automatically revoking access if their risk profile changes.</p>
    `,
    keywords: "Zero Trust, Network Security, IAM"
  },
  {
    id: 40,
    slug: "secure-coding-in-java-script",
    title: "Secure JS Best Practices",
    excerpt: "From sanitization to CSP: A practical guide to writing secure JavaScript in 2024.",
    date: "October 20, 2024",
    readTime: "10 min read",
    category: "Security",
    image: "https://images.unsplash.com/photo-1507721999472-8ed4421c4af2?auto=format&fit=crop&q=80&w=1200",
    author: "Dishant Madariya",
    content: `
      <h2>Your Code is the First Line of Defense</h2>
      <p>Frameworks help, but developers are the ones who write the bugs. Mastering secure coding patterns in JavaScript is the most effective way to protect your users and your company.</p>
      <h3>Content Security Policy (CSP)</h3>
      <p>Learn how to use CSP headers to tell the browser exactly which scripts and styles are allowed to run. This is the single most powerful defense against XSS vulnerabilities.</p>
      <h3>Sanitization vs. Encoding</h3>
      <p>Understand the difference between cleaning input and safely showing it. We'll discuss libraries like <code>DOMPurify</code> and why built-in browser encoding is usually your first and best defense.</p>
      <h3>Handling Third-Party Scripts</h3>
      <p>Your site is only as safe as the least secure script tag you include. We'll look at the "Subresource Integrity" (SRI) attribute and how it ensures that external libraries haven't been tampered with by malicious actors.</p>
    `,
    keywords: "JavaScript Security, XSS, CSP, DOMPurify"
  }
];
